/**
 * Demostración del sistema de sensores, en el área de inversionistas.
 *
 * Regla que gobierna este archivo: **nada de lo que se escriba aquí puede
 * afirmar que algo está desplegado, en producción o midiendo un evento real.**
 * Es una demostración con datos inventados, y el aviso de la pantalla lo dice.
 * Describir capacidades del producto está bien; presentarlas como una medición
 * en curso, no.
 */

export const demo = {
  eyebrow: 'Demostración del producto',
  titulo: 'Lo que ve una marca en su stand',
  subtitulo:
    'De cada cien personas que entran a un stand, unas pocas pasan su gafete. Todo el mundo vende ese puñado. Esto enseña también el resto: quién se quedó, cuánto, con qué cara — y cómo recuperar a los que se fueron sin dejar contacto.',
  avisoDatos: 'Las personas, las cifras y las trayectorias de esta pantalla',

  seccionesLabel: 'Partes del panel',
  secciones: {
    camara: 'Cámara del stand',
    activos: 'Leads activos',
    pasivos: 'Leads pasivos',
    radar: 'Radar mmWave',
  },

  /**
   * El reloj de la jornada. Lo comparten la cámara y las dos tablas de leads.
   *
   * Los multiplicadores son del **tiempo real**, no de la velocidad a la que
   * abre la demo: por eso hace falta decir debajo cuánto dura la jornada
   * entera a la velocidad elegida. Un "480×" a secas no le dice nada a nadie.
   */
  reloj: {
    reproduciendo: 'Reproducción de la jornada',
    jornadaCerrada: 'Jornada cerrada',
    hora: (h: string, m: string) => `${h}:${m}`,

    // El segundero, ya con dos dígitos. Sale solo por debajo de 60×.
    segundos: (s: string) => `:${s}`,

    velocidad: 'Velocidad',
    velocidadLabel: 'Velocidad de reproducción',
    // El signo de multiplicar, no la letra equis.
    multiplo: (n: string) => `${n}×`,
    jornadaEn: (duracion: string) => `La jornada completa en ${duracion}`,
    // Reciben la cifra ya formateada por `useFormato`.
    duracionSegundos: (s: string) => `${s} s`,
    duracionMinutos: (m: string) => `${m} min`,
    duracionHoras: (h: string) => `${h} h`,
  },

  /**
   * La portada de la demostración abierta: un inicio de sesión que no
   * autentica nada.
   *
   * Aquí la regla del archivo aprieta más que en ninguna otra parte: la
   * pantalla imita un login de verdad, así que **tiene que decir que no lo
   * es** — antes del formulario y sin eufemismos.
   */
  /**
   * Aviso de datos simulados. Heredado del portal de marcas, que se fue con el
   * backend: alli distinguia tres origenes y aqui solo hay uno, porque todo lo
   * que se pinta es simulacion.
   */
  aviso: {
    antes: (sujeto: string) => `${sujeto} son `,
    fuerte: 'de demostración',
    despues:
      ': sirven para mostrar cómo se verá la pantalla, no corresponden a un evento real.',
  },

  acceso: {
    correo: 'Correo',
    contrasena: 'Contraseña',
    titulo: 'Panel de marca',
    subtitulo:
      'Así entra una marca al panel de su stand. Lo que sigue es una simulación con datos inventados: no hay cuenta, no hay sesión y no se envía nada a ningún servidor.',
    aviso:
      'Demostración: este acceso siempre abre. Las credenciales vienen puestas y no se comprueban contra nada.',
    credenciales: 'Credenciales de ejemplo, fijas y sin efecto.',
    entrar: 'Entrar al panel',
    volverAlSitio: 'Volver al sitio',
  },

  cierre:
    'La cámara y el radar miden lo mismo de formas distintas y no se suman: una misma persona aparecería dos veces. Por eso el sistema mantiene los conteos separados y obliga a elegir una fuente.',
  // Quien llegó por la ruta abierta no tiene panel al que volver.
  salir: 'Salir de la demostración',

  camara: {
    mirandoAhora: 'Mirando ahora',
    afluencia: 'Entraron al stand',
    atencionMedia: 'Atención media',
    enganche: 'Índice de enganche',

    expresion: 'Con qué cara miraron',
    expresionDetalle:
      'Reparto de expresión de quien estuvo frente al stand, ponderado por el tiempo que estuvo. Quien se quedó seis minutos pesa seis minutos; quien pasó de largo, cuarenta segundos.',
    repartoAlt: (dominante: string) => `Reparto de expresión. Predomina: ${dominante}.`,

    curva: 'Enganche a lo largo del día',
    curvaDetalle:
      'Qué parte de la atención fue interés o sorpresa, minuto a minuto. Sirve para saber a qué hora conviene tener gente en el stand, y a qué hora no pasa nada.',
    curvaAlt: (pct: number) => `Curva de enganche del día. Ahora mismo, ${pct} por ciento.`,

    emociones: {
      interes: 'Interés',
      sorpresa: 'Sorpresa',
      duda: 'Duda',
      neutral: 'Neutral',
      desinteres: 'Desinterés',
    },

    privacidad:
      'La cámara no guarda imágenes ni reconoce rostros. Lo que sale de ella es agregado: cuánta gente miró, cuánto aguantó y con qué expresión, más un tramo de edad estimado. Nunca una edad, nunca una persona.',

    segundos: (s: string) => `${s} s`,
  },

  leads: {
    permanenciaMedia: 'Permanencia media',
    recurrentes: 'Volvieron',
    perfil: 'En qué se parecen',
    perfilDetalle:
      'El perfil del grupo, no de cada persona. Es lo que sirve para decidir a quién traer al stand mañana.',
    segundos: (s: string) => `${s} s`,

    columnas: {
      persona: 'Persona',
      empresa: 'Empresa',
      cargo: 'Cargo',
      edad: 'Edad estimada',
      areas: 'Dónde se paró',
      permanencia: 'Permanencia',
      expresion: 'Expresión',
    },

    cargos: {
      direccion: 'Dirección',
      gerencia: 'Gerencia',
      especialista: 'Especialista',
      otro: 'Otro',
    },

    areas: {
      pantalla: 'Pantalla',
      mesa: 'Mesa',
      producto: 'Producto',
      mostrador: 'Mostrador',
    },

    estados: {
      anonimo: 'Sin identificar',
      rechazo: 'No quiso compartir',
      sin_respuesta: 'No contestó',
    },

    activos: {
      total: 'Leads activos',
      conversion: 'De los que entraron',
      tabla: 'Últimos leads capturados',
      explica:
        'Pasaron su gafete por el lector del stand. Dieron su consentimiento de forma deliberada, así que tienes su contacto completo. Es lo que ya vende todo el mundo.',
    },

    pasivos: {
      total: 'Leads pasivos',
      perdidos: 'De los que entraron',
      tabla: 'Quiénes estuvieron y no dejaron contacto',
      explica: (umbral: number) =>
        `Se quedaron más de ${umbral} segundos y nunca pasaron el gafete. Hoy, para tu marca, esta gente simplemente no ocurrió. La cámara sí los vio, y el padrón del evento sabe quiénes son — pero eso lo sabe Ilumetria, no tú.`,
    },

    campana: {
      titulo: 'Recuperar estos contactos',
      explica:
        'Les escribimos nosotros, con el nombre del evento, y les preguntamos si quieren compartir su contacto contigo. Quien acepta pasa a tu lista; quien no, sigue siendo un número en esta tabla y nunca sabrás quién era.',
      soloAceptados: 'Se cobra por contacto recuperado, no por mensaje enviado.',
      lanzar: (cuantos: number) => `Preguntar a los ${cuantos}`,
      consentimiento:
        'Nadie recibe tus datos ni tú los suyos hasta que la persona dice que sí. Es un consentimiento pedido después de la visita, no una lista comprada.',
      enviando: (van: number, total: number) => `Respondieron ${van} de ${total}…`,
      resuelta: (aceptaron: number, total: number) =>
        `${aceptaron} de ${total} aceptaron compartir su contacto. Ya están en tu lista.`,
      aceptaron: 'Aceptaron',
      rechazaron: 'Dijeron que no',
      callaron: 'No contestaron',
    },
  },

  radar: {
    aforo: 'Aforo ahora',
    visitas: 'Visitas del día',
    entradas: 'Entradas',
    salidas: 'Salidas',

    enVivo: 'Recorrido en curso · 10 cuadros por segundo',
    // A cualquier velocidad que no sea 1×: se dice a qué ritmo se está mirando,
    // porque el plano ya no va al del sensor.
    enVivoRapido: (multiplo: string) => `Recorrido en curso · reproducido a ${multiplo}×`,
    notaVelocidad:
      'El radar difunde a diez cuadros por segundo y esto es una reproducción: la velocidad de arriba mueve el plano igual que mueve el reloj. Lo que no cambia son las cifras — la permanencia y la mediana están en segundos reales del recorrido, y mirarlo más rápido no acorta lo que duró.',
    sinAnimacion: 'Animación desactivada por tus preferencias del sistema',
    mapaDeCalor: 'Mapa de calor',

    porZona: 'Personas por zona',
    notaZonas:
      'El radar entrega coordenadas en metros, así que las zonas se dibujan sobre el plano del stand y la permanencia en cada una sale sola. No hay imágenes ni biometría: es anónimo por física.',

    /** Descripción del lienzo para quien no puede verlo. */
    lienzoAlt: (aforo: number) =>
      `Vista cenital del stand con las trayectorias de los visitantes. Ahora mismo hay ${aforo} personas dentro.`,
    personasEnZona: (cuantas: number, zona: string) => `${cuantas} personas en ${zona}`,

    /** Etiquetas de las zonas. La geometría vive en `data/escenario.ts`. */
    zonas: {
      'stand-1': 'Stand 1',
      'stand-2': 'Stand 2',
      'stand-3': 'Stand 3',
    },

    /* ── Métricas de visitas cerradas ── */

    analitica: 'Lo que deja cada visita',
    analiticaDetalle:
      'El estado en vivo dice cuánta gente hay; esto dice qué hizo cada una. Son las mismas métricas que emite el radar al cerrar una trayectoria.',
    permanenciaMedia: 'Permanencia media',
    permanenciaMediana: 'Mediana',
    notaMediana:
      'La media y la mediana no coinciden, y esa distancia es el dato: quien cruza el pasillo sin mirar hunde la media sin tocar la mediana. Un panel con una sola de las dos haría pensar que nadie se detiene.',
    distanciaMedia: 'Recorrido medio',
    calidad: 'Fragmentos por visita',
    notaCalidad:
      'Cuántos tramos hizo falta unir para reconstruir cada recorrido. Uno es perfecto; por encima de dos, el propio spec del radar considera que la permanencia deja de ser creíble. Se publica porque un sistema de medición que esconde su margen de error no se puede auditar.',
    permanenciaPorZona: 'Permanencia por stand',
    visitantesZona: (n: number) => `${n} visitantes`,
    notaSolapan:
      'Las zonas se solapan a propósito, así que la permanencia por stand puede sumar más que la permanencia total: un segundo dentro del stand 2 es también un segundo dentro del área medida.',
    // Reciben la cifra **ya formateada** por `useFormato`: el separador
    // decimal cambia con el idioma, así que formatearla aquí sería
    // reimplementar `Intl` en el diccionario.
    segundos: (s: string) => `${s} s`,
    metros: (m: string) => `${m} m`,
  },
};
